import { toast } from 'react-toastify';
import { Transaction } from '../../interfaces';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import {
  REFUND_REASON_LABELS,
  RefundPaymentPayload,
  getRefundPaymentSchema,
} from '../../types/payments';
import {
  getPaymentMaxRefundAmount,
  getTransactionCurrency,
} from '../../helpers/payments';
import useRefundPayment from '../../hooks/payments/useRefundPayment';
import useRefreshPayment from '../../hooks/payments/useRefreshPayment';

interface Props {
  transaction: Transaction;
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void | Promise<void>;
}

function TransactionRefundModal({
  transaction,
  open,
  onClose,
  onRefresh,
}: Props) {
  const { mutateAsync: refreshPayment } = useRefreshPayment(onRefresh);

  const { mutation: refundMutation } = useRefundPayment({
    payment: transaction?.payment!,
    disabled: !transaction?.payment,
    onSuccess: async () => {
      await refreshPayment(transaction!.payment!);

      toast.success('Payment refunded successfully.');

      onClose();
    },
    onError: () => {},
  });

  const close = () => {
    if (refundMutation.isPending) return;
    onClose();
  };

  const maxRefundAmount = getPaymentMaxRefundAmount(transaction?.payment);

  const refundPaymentSchema = getRefundPaymentSchema(maxRefundAmount);

  const refundForm: FormData<RefundPaymentPayload> = {
    title: 'Refund Payment',
    validationSchema: refundPaymentSchema,
    initialValues: {
      amount: maxRefundAmount.toFixed(2) as unknown as number,
      reason: '' as any,
      otherReason: '',
    },
    inputRows: [
      [
        {
          name: 'amount',
          label: 'Refund',
          inputType: 'text',
          suffix: getTransactionCurrency(transaction),
          disabled: true,
          tooltip: 'Refund the full amount',
        },
      ],
      [
        {
          name: 'reason',
          label: 'Reason',
          inputType: 'select',
          options: Object.entries(REFUND_REASON_LABELS).map(
            ([value, label]) => ({
              value,
              label,
            }),
          ),
          placeholder: 'Select a reason',
        },
      ],
      [
        {
          name: 'otherReason',
          label: 'Other Reason',
          inputType: 'textarea',
          isVisible: values => values?.reason === 'OTHER',
          placeholder: 'Add a reason',
        },
      ],
    ],
    onSubmit: async data => {
      const body: RefundPaymentPayload = {
        ...data,
        amount: parseFloat(data.amount as unknown as string),
      };
      await refundMutation.mutateAsync(body);
    },
    submitButtonText: 'Refund',
  };

  if (!open) return null;

  return (
    <FormSmallModal
      alert={{
        subtitle:
          "Refunds take 5-10 days to appear on a customer's statement. Fees for the original payment won't be returned, but there are no additional fees for the refund.",
        type: 'info',
        animate: false,
        dismissible: false,
      }}
      open={open && !!transaction?.payment}
      form={refundForm}
      onClose={close}
    />
  );
}

export default TransactionRefundModal;
